import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/styles';
import {Theme} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import Grid from '@material-ui/core/Grid';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import clsx from 'clsx';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import RemoveIcon from '@material-ui/icons/Remove';
import {Group, GroupResourceOption} from '../../../graphql/groups.datatypes';
import _ from 'underscore';
import {
    BillingPartyData,
    BookingData,
    getGuestBaseCost,
    getOptionDetails,
    getTotalBillingPartyCost,
    GuestData
} from '../helperFunctions';
import {PaymentScheduleAction, PaymentScheduleAmountType} from '../../../graphql/payment_schedule.datatypes';
import {formatDate} from '../../../utils/formatters';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        fontSize: 20,
        marginBottom: theme.spacing(1),
    },
    body: {
        fontSize: 16,
        color: theme.palette.black,
    },
    padBottom: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    marBottom: {
        marginTop: theme.spacing(3),
        marBottom: theme.spacing(3),
    },
    heading: {
        fontFamily: 'Recoleta-SemiBold',
    },
    alignRight: {
        textAlign: 'right',
    },
    totalCostBar: {
        position: 'relative',
        backgroundColor: theme.palette.secondary.main,
        paddingTop: 14,
        paddingBottom: 14,
        marginTop: theme.spacing(3.5),
        marginBottom: theme.spacing(3.5),

        '&:before, &:after': {
            content: '""',
            position: 'absolute',
            top: 0,
            width: '100vw',
            height: '100%',
            backgroundColor: 'inherit',
        },

        '&:before': {
            left: '-100vw'
        },

        '&:after': {
            left: '100%'
        }
    },
    totalCostBarText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: theme.palette.white,
    },
    expansionPanel: {
        boxShadow: 'none',
        backgroundColor: 'transparent',

        '&.MuiExpansionPanel-root.Mui-expanded': {
            margin: 0,
        },

        '&.MuiExpansionPanel-root.Mui-expanded:before': {
            opacity: 1,
        },

        '& .MuiExpansionPanelSummary-root': {
            paddingLeft: 0,
            paddingRight: 0,
            borderTopColor: '#B2B2B2',
            borderBottomColor: '#B2B2B2',
            borderStyle: 'solid',
            borderTopWidth: 0,
            borderBottomWidth: 1,
        },

        '& .MuiExpansionPanelSummary-root:first-of-type': {
            borderTopWidth: 1,
        },

        '& .MuiExpansionPanelSummary-root.Mui-expanded': {
            minHeight: theme.spacing(6),
        },

        '& .MuiExpansionPanelSummary-content.Mui-expanded': {
            marginTop: theme.spacing(1.5),
            marginBottom: theme.spacing(1.5),
        },

        '& .MuiExpansionPanelDetails-root': {
            backgroundColor: 'rgba(172, 2, 163, 0.1)',
            padding: 0,

            '& ul': {
                width: '100%',
            },

            '& li': {
                borderTopWidth: 1,
                borderTopStyle: 'solid',
                borderTopColor: 'rgba(178, 178, 178, 0.5)',
                paddingTop: 9,
                paddingBottom: 9,
                paddingLeft: theme.spacing(1.5),
                paddingRight: theme.spacing(1.5),
            },

            '& li:first-of-type': {
                borderTopWidth: 0,
            },
        },
    },
    expansionIcon: {
        marginBottom: -7,
        marginLeft: 8,
        color: theme.palette.secondary.main,
    },
    guestList: {
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(3),
    },
    guestListItem: {
        fontWeight: 'bold',
        listStyle: 'none',
    },
    optionListItem: {
        fontWeight: 'bold',
    },
    guestOptionListItem: {
        color: '#333333',
        listStyle: 'none',
    },
    scheduleHeaderFooter: {
        backgroundColor: '#AD02A3',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),

        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.white,
    },
    scheduleFooter: {
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.white,
        marginBottom: theme.spacing(2.5),
    },
    scheduleHeaderFooterText: {
        color: theme.palette.white,
        fontFamily: 'Nunito',
        fontSize: 16,
        fontWeight: 'bold',
    },
    scheduleBody: {
        backgroundColor: 'rgba(173, 2, 163, 0.1)',
        padding: theme.spacing(2),
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.white,
    },
    scheduleBodyText: {
        fontFamily: 'Nunito',
        fontSize: 16,
        fontWeight: 'bold',
    },
}));

interface Props {
    allGuestData: GuestData[];
    billingParty: BillingPartyData;
    billingPartyIndex: number;
    group: Group;
    bookingData: BookingData;
}

let instancesCount = 0

const BillingPartySummary = (props: Props) => {

    useEffect(() => {
        instancesCount += 1
        return () => {
          instancesCount -= 1
        }
      }, [])

    const {allGuestData, billingParty, billingPartyIndex, group, bookingData} = props;
    const classes = useStyles();
    const [openedSections, setOpenedSections] = useState<number[]>([]);

    const guests = allGuestData.filter((guestData) => guestData.billingPartyId === billingParty.id);

    const options: GroupResourceOption[] = _.uniq(_.flatten(
        allGuestData.map(guestData => guestData.optionValues
            .filter(optionValue => optionValue.value > 0 && !optionValue.groupResourceOption.is_hidden)
            .map(optionValue => optionValue.optionId))
    )).map(optionId => group.options!.find(option => option.option.id === optionId)!);

    const handleSectionToggle = (sectionIndex: number) => {
        if (openedSections.includes(sectionIndex)) {
            setOpenedSections(openedSections.filter(sIndex => sIndex !== sectionIndex));
        } else {
            setOpenedSections([...openedSections, sectionIndex]);
        }
    };

    const partyTotal = getTotalBillingPartyCost(group, billingParty, bookingData, allGuestData);
    const paymentDates = group.payment_schedule!.dates!
        .filter(date => date.action_type === PaymentScheduleAction.PAYMENT)
        .sort((a, b) => moment(a.due_date!).unix() - moment(b.due_date!).unix());

    let runningTotal = 0;
    paymentDates.map(date => {
        switch (date.amount_type) {
            case PaymentScheduleAmountType.FIXED:
                date.calculatedAmount = date.amount;
                runningTotal += date.calculatedAmount!;
                break;
            case PaymentScheduleAmountType.PERCENT:
                date.calculatedAmount = Math.ceil(partyTotal * (date.amount! / 100));
                runningTotal += date.calculatedAmount!;
                break;
            case PaymentScheduleAmountType.FIXED_PER_PERSON:
                date.calculatedAmount = date.amount! * guests.length;
                runningTotal += date.calculatedAmount!;
                break;
            case PaymentScheduleAmountType.PERCENT_PER_PERSON:
                date.calculatedAmount = Math.ceil(partyTotal * (date.amount! / 100)) * guests.length;
                runningTotal += date.calculatedAmount!;
                break;
            case PaymentScheduleAmountType.REMAINING:
                date.calculatedAmount = partyTotal - runningTotal;
                break;
        }
    });

// if statement for checking if more than one billing party
    const indextot = billingPartyIndex + 1;
    const partyCheck = instancesCount > 1 ? indextot.toString() : '';

    return (
        <React.Fragment>
            {billingPartyIndex === 1 && (
                <Typography variant="h1" className={clsx([classes.heading, classes.padBottom])}>
                    Other Billing Parties
                </Typography>
            )}

            <Grid container>
                <Grid item xs={12}>
                    <Typography variant="h3" className={classes.header}>
                        {billingParty.first_name} {billingParty.last_name}:<br/>
                        Billing Party {partyCheck}
                    </Typography>

                    <Typography variant="body1" className={classes.body}>
                    This billing party includes the following travelers:
                    </Typography>

                    <ul className={classes.guestList}>
                        {guests.map((guest, guestIndex) => (
                            <Grid key={guestIndex} container>
                                <Grid item xs={6}>
                                    <Typography variant="body1"
                                                className={clsx(classes.body, classes.optionListItem)}>
                                        {guestIndex + 1}. {guest.first_name} {guest.last_name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} className={classes.alignRight}>
                                    <Typography variant="body1"
                                                className={clsx(classes.body, classes.optionListItem)}>
                                        ${getGuestBaseCost(group, bookingData, guest)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))}
                    </ul>
                </Grid>

                <Grid item xs={12}>
                    {options.map((option, optionIndex) => {
                        const optionDetails = getOptionDetails(option)!;
                        const optionGuests = guests.filter(guest => !!guest.optionValues.find(optionVal => optionVal.optionId === option.option.id))

                        return (
                            <ExpansionPanel key={optionIndex} square className={classes.expansionPanel}>
                                <ExpansionPanelSummary onClick={() => handleSectionToggle(optionIndex)}>
                                    <Grid container>
                                        <Grid item xs={6}>
                                            <Typography variant="body1"
                                                        className={clsx(classes.body, classes.optionListItem)}>
                                                {optionDetails.title}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} className={classes.alignRight}>
                                            <Typography variant="body1"
                                                        className={clsx(classes.body, classes.optionListItem)}>
                                                ${optionGuests.reduce((total, guest) => {
                                                    const guestOption = guest.optionValues.find(optionVal => optionVal.optionId === option.option.id)!;
                                                    return total + (optionDetails.cost * guestOption.value);
                                                }, 0)}
                                                {openedSections.includes(optionIndex) ?
                                                    <RemoveIcon className={classes.expansionIcon}/> :
                                                    <AddIcon className={classes.expansionIcon}/>
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </ExpansionPanelSummary>
                                <ExpansionPanelDetails>
                                    <ul>
                                        {optionGuests.map((guest, optionGuestIndex) => {
                                            const guestOption = guest.optionValues.find(optionVal => optionVal.optionId === option.option.id)!;
                                            return guestOption.value > 0 && (
                                                <Grid container key={optionGuestIndex} component="li"
                                                      className={classes.guestOptionListItem}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body1" className={classes.body}>
                                                            {optionGuestIndex + 1}. {guest.first_name} {guest.last_name}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} className={classes.alignRight}>
                                                        <Typography variant="body1" className={classes.body}>
                                                            ${optionDetails.cost * guestOption.value}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                    </ul>
                                </ExpansionPanelDetails>
                            </ExpansionPanel>
                        );
                    })}
                </Grid>

                <Grid container item xs={12} className={classes.totalCostBar}>
                    <Grid item xs={6}>
                        <Typography variant="body1" className={classes.totalCostBarText}>
                              Billing Party {partyCheck} Total
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className={classes.alignRight}>
                        <Typography variant="body1" className={classes.totalCostBarText}>
                            ${partyTotal}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            {indextot === 1 &&

          <React.Fragment>
            <Typography variant="h1" className={clsx([classes.heading, classes.padBottom])}>
                Payment Schedule
            </Typography>

            <Grid container className={classes.padBottom}>
                {paymentDates.map((date, i) => {
                    const headerFooter = (i === paymentDates.length - 1);

                    return (
                        <Grid item xs={12} key={i}
                              className={headerFooter ? classes.scheduleHeaderFooter : classes.scheduleBody}>
                            <Typography variant="body1"
                                        className={headerFooter ? classes.scheduleHeaderFooterText : classes.scheduleBodyText}>
                                {date.title}:&nbsp;
                                {headerFooter && (
                                    <React.Fragment>
                                        ${date.calculatedAmount}
                                    </React.Fragment>
                                )}
                                {i === paymentDates.length - 1 && (
                                    <React.Fragment> DUE {formatDate(date.due_date)}</React.Fragment>
                                )}
                            </Typography>
                            {!headerFooter && (
                                <Typography variant="body1" className={classes.scheduleBodyText}>
                                  {date.amount_type === PaymentScheduleAmountType.FIXED && ('$' +date.calculatedAmount+ ' ')}
                                  {date.amount_type === PaymentScheduleAmountType.FIXED_PER_PERSON && ('$'+date.amount+' PER PERSON ')}
                                  {date.amount_type === PaymentScheduleAmountType.PERCENT && ('$'+date.calculatedAmount+' ')}
                                  {date.amount_type === PaymentScheduleAmountType.PERCENT_PER_PERSON && ('$'+date.amount+' PER PERSON ')}
                                  {date.amount_type === PaymentScheduleAmountType.REMAINING && ('$'+date.calculatedAmount+' ')}

                                    {i !== 0 && <React.Fragment>, DUE <strong>{formatDate(date.due_date)}</strong></React.Fragment>}
                                </Typography>
                            )}
                        </Grid>
                    )
                })}

                {paymentDates.length > 0 && paymentDates[0] && (
                    <Grid item xs={12} key={-1} className={clsx([classes.scheduleHeaderFooter, classes.marBottom, classes.padBottom])} >
                        <Typography variant="body1" className={classes.scheduleHeaderFooterText}>
                            First Deposit Due Now: ${paymentDates[0].calculatedAmount}
                        </Typography>
                    </Grid>
                )}

            </Grid>
              <Typography variant="body1" className={classes.body}>
                  If you are registering after the 1st deposit date listed in the above schedule you may be responsible to pay more of a deposit to be current on all deposits due based on schedule above.
              </Typography>
            </React.Fragment>
          }
            <br/><br/>
        </React.Fragment>
    );
};

export default BillingPartySummary;
